/**
 * Typed WebSocket event protocol.
 *
 * Every frame on `/api/ws` is one of `ClientEvent | ServerEvent`. The union is
 * exhaustive in both directions: `parseClientEvent` and `parseServerEvent` are
 * total functions, and both are covered by protocol tests.
 *
 * Wire format: UTF-8 JSON text frames for control events, binary frames for
 * audio. Binary frames are `audio.chunk` in disguise and are documented in
 * API.md; `isAudioFrame` detects them.
 */

import type { AudioOutputMode, ConversationMode, SessionState, SpeakerId } from "../constants/index.js";
import type { ErrorCode } from "../constants/index.js";
import type { LanguageCode, LanguageDetection } from "./language.js";
import type { TranscriptEntry } from "./transcript.js";
import type { ProviderHealth } from "./provider.js";

// ---------------------------------------------------------------------------
//  Shared envelopes
// ---------------------------------------------------------------------------

export interface EventEnvelope {
  /** Monotonically increasing per connection. Lets the client drop stale frames. */
  readonly seq: number;
  /** Server epoch ms when the event was produced. */
  readonly ts: number;
  /** Correlates a request with its response. Echoed from the client event. */
  readonly requestId?: string;
}

export type ClientEventEnvelope<T extends ClientEvent["type"]> = EventEnvelope & Extract<ClientEvent, { type: T }>;

export type ServerEventEnvelope<T extends ServerEvent["type"]> = EventEnvelope & Extract<ServerEvent, { type: T }>;

/** A parsed server frame: the event plus the transport metadata. */
export type ServerFrame = ServerEvent & EventEnvelope;

/** Language selection. `auto` asks the server to detect. */
export type LanguageSelection = LanguageCode | "auto";

/** A slice of a sentence that is still being spoken. */
export interface PartialResult {
  readonly text: string;
  /** 0..1. Provider confidence, or `null` when the provider reports none. */
  readonly confidence: number | null;
  /** Wall-clock ms since the segment opened. */
  readonly elapsedMs: number;
}

/** A completed, translated utterance. */
export interface FinalUtterance {
  readonly id: string;
  readonly speaker: SpeakerId;
  readonly sourceLanguage: LanguageCode;
  readonly targetLanguage: LanguageCode;
  readonly sourceText: string;
  readonly translatedText: string;
  /** True when the source and target are the same language. */
  readonly passthrough: boolean;
  /** True when a model actually produced the translation. */
  readonly translated: boolean;
  /** Epoch ms when the segment started. */
  readonly startedAt: number;
  /** Epoch ms when the segment was finalized. */
  readonly endedAt: number;
  /** Client-recognizer offset, for subtitle timing. */
  readonly startMs: number;
  readonly endMs: number;
  /** Wall-clock ms spent transcribing the segment. */
  readonly transcriptionMs: number;
  /** Wall-clock ms spent translating. */
  readonly translationMs: number;
  readonly detection: LanguageDetection | null;
}

// ---------------------------------------------------------------------------
//  CLIENT -> SERVER
// ---------------------------------------------------------------------------

export type ClientEvent =
  | { readonly type: "session.start"; readonly sourceLanguage: LanguageSelection; readonly targetLanguage: LanguageCode; readonly mode: ConversationMode; readonly audioOutput: AudioOutputMode; readonly ttsEnabled: boolean; readonly speakers: readonly SpeakerId[]; readonly persistTranscript: boolean }
  | { readonly type: "audio.start"; readonly speaker: SpeakerId; readonly sampleRate: number; readonly encoding: "linear16" }
  | { readonly type: "audio.stop"; readonly speaker: SpeakerId }
  /** Binary frames are implicit; this event carries audio over a text frame for debugging. */
  | { readonly type: "audio.chunk"; readonly speaker: SpeakerId; readonly base64: string }
  | { readonly type: "audio.flush"; readonly speaker: SpeakerId }
  | { readonly type: "translation.request"; readonly texts: readonly string[]; readonly sourceLanguage: LanguageSelection; readonly targetLanguage: LanguageCode; readonly requestId: string }
  | { readonly type: "detection.request"; readonly text: string; readonly requestId: string }
  | { readonly type: "tts.request"; readonly text: string; readonly language: LanguageCode; readonly requestId: string }
  | { readonly type: "language.change"; readonly sourceLanguage: LanguageSelection; readonly targetLanguage: LanguageCode }
  | { readonly type: "conversation.reset" }
  | { readonly type: "settings.update"; readonly audioOutput: AudioOutputMode; readonly ttsEnabled: boolean; readonly persistTranscript: boolean }
  | { readonly type: "speaker.assign"; readonly speaker: SpeakerId; readonly language: LanguageSelection }
  | { readonly type: "ping" }
  | { readonly type: "session.end" };

// ---------------------------------------------------------------------------
//  SERVER -> CLIENT
// ---------------------------------------------------------------------------

export type ServerEvent =
  | {
      readonly type: "session.ready";
      readonly sessionId: string;
      readonly protocolVersion: number;
      readonly state: SessionState;
      readonly serverTime: number;
      readonly sourceLanguage: LanguageSelection;
      readonly targetLanguage: LanguageCode;
      readonly mode: ConversationMode;
      readonly audioOutput: AudioOutputMode;
      readonly ttsEnabled: boolean;
      readonly persistTranscript: boolean;
      readonly speakers: readonly SpeakerId[];
      readonly maxSessionSeconds: number;
      readonly remainingSeconds: number;
      readonly limits: Readonly<Record<string, number>>;
      readonly providers: readonly ProviderHealth[];
    }
  | { readonly type: "session.state"; readonly state: SessionState; readonly detail?: string }
  | { readonly type: "transcription.partial"; readonly speaker: SpeakerId; readonly text: string; readonly confidence: number | null; readonly elapsedMs: number }
  | { readonly type: "transcription.final"; readonly utterance: FinalUtterance }
  | { readonly type: "translation.partial"; readonly utteranceId: string; readonly speaker: SpeakerId; readonly text: string; readonly sourceLanguage: LanguageCode; readonly targetLanguage: LanguageCode }
  | { readonly type: "translation.final"; readonly utterance: FinalUtterance }
  | { readonly type: "language.detected"; readonly detection: LanguageDetection; readonly override: boolean }
  | { readonly type: "tts.started"; readonly utteranceId: string; readonly language: LanguageCode }
  | { readonly type: "tts.chunk"; readonly utteranceId: string; readonly base64: string; readonly index: number; readonly final: boolean }
  | { readonly type: "tts.completed"; readonly utteranceId: string }
  | { readonly type: "transcript.entry"; readonly entry: TranscriptEntry }
  | { readonly type: "transcript.cleared" }
  | { readonly type: "transcript.persisted"; readonly transcriptId: string | null }
  | { readonly type: "translation.result"; readonly requestId: string; readonly results: readonly TranslationResult[]; readonly ms: number }
  | { readonly type: "detection.result"; readonly requestId: string; readonly detection: LanguageDetection }
  | { readonly type: "tts.result"; readonly requestId: string; readonly base64: string; readonly mimeType: string }
  | { readonly type: "quota"; readonly remaining: number; readonly resetsAt: number; readonly reason: string }
  | { readonly type: "ping"; readonly serverTime: number }
  | { readonly type: "pong"; readonly serverTime: number }
  | { readonly type: "error"; readonly code: ErrorCode; readonly message: string; readonly retryable: boolean; readonly detail?: string; readonly requestId?: string };

export interface TranslationResult {
  readonly sourceText: string;
  readonly translatedText: string;
  readonly sourceLanguage: LanguageCode;
  readonly targetLanguage: LanguageCode;
  readonly detectedSourceLanguage: LanguageDetection | null;
  readonly passthrough: boolean;
  readonly ms: number;
}

// ---------------------------------------------------------------------------
//  Guards
// ---------------------------------------------------------------------------

const CLIENT_EVENT_TYPES: ReadonlySet<string> = new Set<ClientEvent["type"]>([
  "session.start",
  "audio.start",
  "audio.stop",
  "audio.chunk",
  "audio.flush",
  "translation.request",
  "detection.request",
  "tts.request",
  "language.change",
  "conversation.reset",
  "settings.update",
  "speaker.assign",
  "ping",
  "session.end",
]);

const SERVER_EVENT_TYPES: ReadonlySet<string> = new Set<ServerEvent["type"]>([
  "session.ready",
  "session.state",
  "transcription.partial",
  "transcription.final",
  "translation.partial",
  "translation.final",
  "language.detected",
  "tts.started",
  "tts.chunk",
  "tts.completed",
  "transcript.entry",
  "transcript.cleared",
  "transcript.persisted",
  "translation.result",
  "detection.result",
  "tts.result",
  "quota",
  "ping",
  "pong",
  "error",
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Total parser for inbound client frames.
 *
 * Returns `null` for anything malformed rather than throwing, so a hostile or
 * buggy client can never take a Durable Object down.
 */
export function parseClientEvent(raw: string): ClientEvent | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;

  const type = parsed["type"];
  if (typeof type !== "string" || !CLIENT_EVENT_TYPES.has(type)) return null;

  const envelope = { seq: numOr(parsed["seq"], 0), ts: numOr(parsed["ts"], 0) };

  switch (type) {
    case "session.start": {
      const speakers = strArray(parsed["speakers"]);
      return {
        ...envelope,
        type,
        sourceLanguage: strOr(parsed["sourceLanguage"], "auto"),
        targetLanguage: strOr(parsed["targetLanguage"], "en"),
        mode: parsed["mode"] === "two-way" ? "two-way" : "solo",
        audioOutput: audioOutputOr(parsed["audioOutput"]),
        ttsEnabled: boolOr(parsed["ttsEnabled"], true),
        speakers: speakers.length > 0 ? (speakers as SpeakerId[]) : (["A"] as SpeakerId[]),
        persistTranscript: boolOr(parsed["persistTranscript"], false),
      };
    }
    case "audio.start":
      return {
        ...envelope,
        type,
        speaker: speakerOr(parsed["speaker"]),
        sampleRate: numOr(parsed["sampleRate"], 16_000),
        encoding: "linear16",
      };
    case "audio.stop":
      return { ...envelope, type, speaker: speakerOr(parsed["speaker"]) };
    case "audio.flush":
      return { ...envelope, type, speaker: speakerOr(parsed["speaker"]) };
    case "audio.chunk":
      return {
        ...envelope,
        type,
        speaker: speakerOr(parsed["speaker"]),
        base64: strOr(parsed["base64"], ""),
      };
    case "translation.request": {
      const texts = strArray(parsed["texts"]);
      return {
        ...envelope,
        type,
        texts: texts as string[],
        sourceLanguage: strOr(parsed["sourceLanguage"], "auto"),
        targetLanguage: strOr(parsed["targetLanguage"], "en"),
        requestId: strOr(parsed["requestId"], ""),
      };
    }
    case "detection.request":
      return { ...envelope, type, text: strOr(parsed["text"], ""), requestId: strOr(parsed["requestId"], "") };
    case "tts.request":
      return {
        ...envelope,
        type,
        text: strOr(parsed["text"], ""),
        language: strOr(parsed["language"], "en"),
        requestId: strOr(parsed["requestId"], ""),
      };
    case "language.change":
      return {
        ...envelope,
        type,
        sourceLanguage: strOr(parsed["sourceLanguage"], "auto"),
        targetLanguage: strOr(parsed["targetLanguage"], "en"),
      };
    case "conversation.reset":
      return { ...envelope, type };
    case "settings.update":
      return {
        ...envelope,
        type,
        audioOutput: audioOutputOr(parsed["audioOutput"]),
        ttsEnabled: boolOr(parsed["ttsEnabled"], true),
        persistTranscript: boolOr(parsed["persistTranscript"], false),
      };
    case "speaker.assign":
      return { ...envelope, type, speaker: speakerOr(parsed["speaker"]), language: strOr(parsed["language"], "auto") };
    case "ping":
      return { ...envelope, type };
    case "session.end":
      return { ...envelope, type };
    default:
      return null;
  }
}

/** Total parser for outbound frames. Mirrors `parseClientEvent`. */
export function parseServerEvent(raw: string): ServerFrame | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;

  const type = parsed["type"];
  if (typeof type !== "string" || !SERVER_EVENT_TYPES.has(type)) return null;

  const envelope = { seq: numOr(parsed["seq"], 0), ts: numOr(parsed["ts"], 0) };

  switch (type) {
    case "session.ready":
    case "session.state":
      // Rich server-side payloads are trusted once the discriminator is a
      // known member of the union; only the wire shape was validated above.
      return { ...envelope, ...parsed, type } as unknown as ServerFrame;
    case "transcription.partial":
      return {
        ...envelope,
        type,
        speaker: speakerOr(parsed["speaker"]),
        text: strOr(parsed["text"], ""),
        confidence: numOrNull(parsed["confidence"]),
        elapsedMs: numOr(parsed["elapsedMs"], 0),
      };
    case "transcription.final": {
      const utterance = parsed["utterance"];
      return isRecord(utterance) ? { ...envelope, type, utterance: utterance as unknown as FinalUtterance } : null;
    }
    case "translation.partial":
      return {
        ...envelope,
        type,
        utteranceId: strOr(parsed["utteranceId"], ""),
        speaker: speakerOr(parsed["speaker"]),
        text: strOr(parsed["text"], ""),
        sourceLanguage: strOr(parsed["sourceLanguage"], "auto"),
        targetLanguage: strOr(parsed["targetLanguage"], "en"),
      };
    case "language.detected": {
      const detection = parsed["detection"];
      return isRecord(detection)
        ? { ...envelope, type, detection: detection as unknown as LanguageDetection, override: boolOr(parsed["override"], false) }
        : null;
    }
    case "tts.started":
      return { ...envelope, type, utteranceId: strOr(parsed["utteranceId"], ""), language: strOr(parsed["language"], "en") };
    case "tts.chunk":
      return {
        ...envelope,
        type,
        utteranceId: strOr(parsed["utteranceId"], ""),
        base64: strOr(parsed["base64"], ""),
        index: numOr(parsed["index"], 0),
        final: boolOr(parsed["final"], true),
      };
    case "tts.completed":
      return { ...envelope, type, utteranceId: strOr(parsed["utteranceId"], "") };
    case "transcript.cleared":
      return { ...envelope, type };
    case "quota":
      return {
        ...envelope,
        type,
        remaining: numOr(parsed["remaining"], 0),
        resetsAt: numOr(parsed["resetsAt"], 0),
        reason: strOr(parsed["reason"], ""),
      };
    case "ping":
      return { ...envelope, type, serverTime: numOr(parsed["serverTime"], 0) };
    case "pong":
      return { ...envelope, type, serverTime: numOr(parsed["serverTime"], 0) };
    case "error":
      return {
        ...envelope,
        type,
        code: strOr(parsed["code"], "internal") as ErrorCode,
        message: strOr(parsed["message"], "Unknown error"),
        retryable: boolOr(parsed["retryable"], false),
        detail: typeof parsed["detail"] === "string" ? parsed["detail"] : undefined,
        requestId: typeof parsed["requestId"] === "string" ? parsed["requestId"] : undefined,
      };
    case "translation.result": {
      const results = parsed["results"];
      return isRecord(parsed) && Array.isArray(results)
        ? ({
            ...envelope,
            type,
            requestId: strOr(parsed["requestId"], ""),
            results: results as unknown as TranslationResult[],
            ms: numOr(parsed["ms"], 0),
          } as unknown as ServerFrame)
        : null;
    }
    case "detection.result": {
      const detection = parsed["detection"];
      return isRecord(detection) ? { ...envelope, type, requestId: strOr(parsed["requestId"], ""), detection: detection as unknown as LanguageDetection } : null;
    }
    default:
      // Events carrying complex payloads (transcript.entry, tts.result) are
      // trusted once the `type` is a known member of the union.
      return { ...envelope, ...parsed, type } as unknown as ServerFrame;
  }
}

/** Adds the envelope fields the server owns. */
export function stamp<T extends ServerEvent | ClientEvent>(event: T, seq: number, ts: number): T & EventEnvelope {
  return { ...event, seq, ts } as T & EventEnvelope;
}

export const isAudioFrame = (data: unknown): data is ArrayBuffer =>
  data instanceof ArrayBuffer || (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(data as ArrayBufferView));

// ---------------------------------------------------------------------------
//  Coercion helpers
// ---------------------------------------------------------------------------

const SPEAKER_SET: ReadonlySet<string> = new Set(["A", "B", "C", "D"]);

function speakerOr(value: unknown): SpeakerId {
  return typeof value === "string" && SPEAKER_SET.has(value) ? (value as SpeakerId) : "A";
}

function audioOutputOr(value: unknown): AudioOutputMode {
  return value === "translated" || value === "both" ? value : "off";
}

function numOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function numOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function strOr(value: unknown, fallback: string): string {
  return typeof value === "string" && value.length > 0 ? value : fallback;
}

function boolOr(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function strArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string");
}
